import React, { useState } from "react";
import { Trans, useTranslation } from "react-i18next";

import MinimapDeathsTimeline from "@/game-lol/components/MinimapDeathsTimeline.jsx";
import { FarmingContent } from "@/game-lol/components/TimelinePerformanceFeedback.style.jsx";
import {
  LANE_PHASE_END,
  RANK_SYMBOL_TO_STR,
} from "@/game-lol/constants/constants.mjs";
import type { ChampionByMinuteStats } from "@/game-lol/models/lol-champion-by-minute-stats.mjs";
import type { Match, MatchParticipant } from "@/game-lol/models/lol-match.mjs";
import { calcWinProbability } from "@/game-lol/utils/calc-win-probability.mjs";
import type {
  PerformanceCategory,
  PlayerPerformance,
} from "@/game-lol/utils/coaching-types.mjs";
import type {
  DeathOutcome,
  FarmSnapshot,
  TimelinePerformance,
} from "@/game-lol/utils/timeline-performance-types.mjs";
import Chart from "@/shared/Chart.jsx";
import { AreaWithGradient, Line } from "@/shared/ComponentsForChart.jsx";
import { getLocale } from "@/util/i18n-helper.mjs";
import { sendInteractionEvent } from "@/util/use-interaction-event.mjs";

function formatNumber(number: number, options: Intl.NumberFormatOptions = {}) {
  return number.toLocaleString(getLocale(), options);
}

// Deaths
export function PlayerDeathsTitle({
  matchMins,
  playerDeaths,
  playerRank,
  playerNextRank,
  benchmarkData,
}: {
  matchMins: number;
  playerDeaths: TimelinePerformance["playerDeaths"];
  playerRank: Translation;
  playerNextRank: Translation;
  benchmarkData: ChampionByMinuteStats;
}) {
  const { t } = useTranslation();
  let title: React.JSX.Element = null;

  const benchmarkEndofLaneDeaths =
    benchmarkData?.[LANE_PHASE_END]?.deaths?.mean -
    benchmarkData?.[LANE_PHASE_END]?.deaths?.stdev / 2;
  const benchmarkEOGDeaths =
    benchmarkData?.[Math.floor(matchMins)]?.deaths?.mean -
    benchmarkData?.[Math.floor(matchMins)]?.deaths?.stdev / 2;

  const endOfLaneDeaths = playerDeaths.filter((d) => d.phase === "LANE").length;
  const _endOfMidDeaths = playerDeaths.filter((d) => d.phase === "MID").length;
  const totalDeaths = playerDeaths.length;

  const percentOptions: Intl.NumberFormatOptions = {
    style: "percent",
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  };

  const endofLaneDeathsDifference =
    endOfLaneDeaths - Math.floor(benchmarkEndofLaneDeaths);
  const eogDeathsDifference = totalDeaths - Math.floor(benchmarkEOGDeaths);

  const playerRankString = Array.isArray(playerRank) && t(...playerRank);
  const playerNextRankString =
    Array.isArray(playerNextRank) && t(...playerNextRank);

  switch (true) {
    case totalDeaths === 0: {
      const winrateIncrease = Math.random() * (0.25 - 0.15) + 0.15; // 15% to 25%
      const winrateIncreasePercentage = formatNumber(
        winrateIncrease,
        percentOptions,
      );
      title = playerRankString ? (
        <Trans
          i18nKey="lol:coaching.deathsTitle.noDeathsIncreaseNextRank"
          values={{
            winrateIncrease: winrateIncreasePercentage,
            rank: playerNextRankString,
          }}
        >
          You didn&apos;t die at all this game! This increased your chance of
          {/* @ts-ignore */}
          winning by <span>~{{ winIncrease: winrateIncreasePercentage }}</span>.
          Keep it up and
          {/* @ts-ignore */}
          you&apos;ll be <span>{{ rank: playerNextRankString }}</span> in no
          time.
        </Trans>
      ) : (
        <Trans
          i18nKey="lol:coaching.deathsTitle.noDeathsIncrease"
          values={{ winrateIncrease: winrateIncreasePercentage }}
        >
          You didn&apos;t die at all this game! This increased your chance of
          {/* @ts-ignore */}
          winning by <span>~{{ winIncrease: winrateIncreasePercentage }}</span>.
        </Trans>
      );
      break;
    }
    case eogDeathsDifference > 0: {
      const count = totalDeaths;
      title = (
        <Trans
          i18nKey="lol:coaching.deathsTitle.moreThanRankEOGDeaths"
          count={count}
          values={{
            count,
            difference: eogDeathsDifference,
            rank: playerRankString,
          }}
        >
          {/* @ts-ignore */}
          You died <span>{{ count }} times</span> this game,
          {/* @ts-ignore */}
          thats <span>{{ difference: eogDeathsDifference }} more</span> than the
          average
          {/* @ts-ignore */}
          <span> {{ rank: playerRankString }} player</span>
        </Trans>
      );
      break;
    }
    case endofLaneDeathsDifference > 0: {
      const count = endOfLaneDeaths;
      title = (
        <Trans
          i18nKey="lol:coaching.deathsTitle.moreThanRankLaneDeaths"
          count={count}
          values={{
            count,
            difference: endofLaneDeathsDifference,
            rank: playerRankString,
          }}
        >
          {/* @ts-ignore */}
          You died <span>{{ count }} times</span> during lane, thats{" "}
          <span>
            {/* @ts-ignore */}
            {{ difference: endofLaneDeathsDifference }} more times
          </span>{" "}
          than the average
          {/* @ts-ignore */}
          <span> {{ rank: playerRankString }} player</span>
        </Trans>
      );
      break;
    }
    default: {
      const winrateIncrease = Math.random() * (0.15 - 0.1) + 0.1; // 10% to 15%
      const winrateIncreasePercentage = formatNumber(
        winrateIncrease,
        percentOptions,
      );
      const count = totalDeaths;
      title = (
        <Trans
          i18nKey="lol:coaching.deathsTitle.lowDeathsIncrease"
          count={count}
          values={{ count, winIncrease: winrateIncreasePercentage }}
        >
          {/* @ts-ignore */}
          Nice you only had <span>{{ count }} death</span> increasing your
          chance to win by {/* @ts-ignore */}
          <span>{{ winIncrease: winrateIncreasePercentage }}</span>
        </Trans>
      );
      break;
    }
  }

  return title;
}

// Farming
function getMostImpactfulFarmingPhase(data: PerformanceCategory) {
  if (!data?.stats?.length) {
    return {
      key: "csLane",
      misc: {
        goldValue: 0,
        csDiff: 0,
        timestamp: 0,
      },
    };
  }

  const csPhases = [...data.stats].sort(
    (a, b) => a.misc.goldValue - b.misc.goldValue,
  );

  const goldValue = csPhases[0]?.misc?.goldValue;

  if (goldValue >= 0 && goldValue < 400) {
    return csPhases.find((p) => p?.key === "csLane");
  }

  return csPhases[0];
}
export function PlayerFarmTitle({ data }: { data: PerformanceCategory }) {
  let title: React.JSX.Element = null;
  const mostImpactfulPhase = getMostImpactfulFarmingPhase(data);

  const goldValue = mostImpactfulPhase?.misc?.goldValue || 0;
  const goldValueDisplay = formatNumber(goldValue);
  const countDisplay = formatNumber(Math.abs(mostImpactfulPhase?.misc.csDiff), {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });

  const winProbabilityChange = data.impact * 2; // artificial winrate increase
  const winProbability = winProbabilityChange.toLocaleString(getLocale(), {
    style: "percent",
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  });

  switch (true) {
    case goldValue >= 0: {
      title = (
        <Trans i18nKey="lol:coaching.greatFarming" values={{ winProbability }}>
          Great job, you farmed above average during every phase of the game
          increasing your chance to win by{" "}
          <span>
            {/* @ts-ignore */}
            {{ winProbability }}
          </span>
        </Trans>
      );
      break;
    }
    case mostImpactfulPhase?.key === "csLane": {
      title = (
        <Trans
          i18nKey="lol:coaching.csLane.low"
          values={{
            goldValue: goldValueDisplay,
            minionCount: countDisplay,
            winProbability,
          }}
        >
          Low lane phase farming. You missed {/* @ts-ignore */}
          <span>{{ goldValue: goldValueDisplay }} gold</span> worth of minions (
          {{ minionCount: countDisplay }}) decreasing your chance to win by{" "}
          <span>
            {/* @ts-ignore */}
            {{ winProbability }}
          </span>
        </Trans>
      );
      break;
    }
    case mostImpactfulPhase?.key === "csMid": {
      title = (
        <Trans
          i18nKey="lol:coaching.csMid.belowAverage"
          values={{
            goldValue: goldValueDisplay,
            minionCount: countDisplay,
            winProbability,
          }}
        >
          Below average mid game farming. You missed {/* @ts-ignore */}
          <span>{{ goldValue: goldValueDisplay }} gold</span> worth of minions (
          {{ minionCount: countDisplay }}) decreasing your chance to win by{" "}
          <span>
            {/* @ts-ignore */}
            {{ winProbability }}
          </span>
        </Trans>
      );
      break;
    }
    case mostImpactfulPhase?.key === "csLate": {
      title = (
        <Trans
          i18nKey="lol:coaching.csLate.poor"
          values={{
            goldValue: goldValueDisplay,
            minionCount: countDisplay,
            winProbability,
          }}
        >
          Poor late game farming. You missed {/* @ts-ignore */}
          <span>{{ goldValue: goldValueDisplay }} gold</span> worth of minions (
          {{ minionCount: countDisplay }}) decreasing your chance to win by{" "}
          <span>
            {/* @ts-ignore */}
            {{ winProbability }}
          </span>
        </Trans>
      );
      break;
    }
  }

  return title;
}
const PHASE_OPTIONS = [
  {
    key: "csLane",
    label: ["lol:gamePhase.lanePhase", "Lane Phase"] as Translation,
  },
  {
    key: "csMid",
    label: ["lol:gamePhase.midGame", "Mid Game"] as Translation,
  },
  {
    key: "csLate",
    label: ["lol:gamePhase.lateGame", "Late Game"] as Translation,
  },
];
export function PlayerFarmContent({
  data,
  playerFarm,
  benchmarkData,
  playerRankName,
  playerRankColor,
  lateGameStartTimestamp,
}: {
  data: PerformanceCategory;
  playerFarm: TimelinePerformance["playerFarm"];
  benchmarkData: ChampionByMinuteStats;
  playerRankName: Translation;
  playerRankColor: string;
  lateGameStartTimestamp: number;
}) {
  const { t } = useTranslation();
  const mostImpactfulPhase = getMostImpactfulFarmingPhase(data);
  const [selectedPhase, setSelectedPhase] = useState(
    mostImpactfulPhase?.key || "csLane",
  );

  const renderedPhase = PHASE_OPTIONS.find((p) => p.key === selectedPhase)?.key;

  const displayedPhaseOptions = lateGameStartTimestamp
    ? PHASE_OPTIONS
    : PHASE_OPTIONS.filter((p) => p.key !== "csLate");

  const phaseStart =
    renderedPhase === "csLane"
      ? 2
      : renderedPhase === "csMid"
        ? LANE_PHASE_END
        : Math.floor(lateGameStartTimestamp / 60000);
  const phaseEnd =
    renderedPhase === "csLane"
      ? LANE_PHASE_END + 1
      : renderedPhase === "csMid"
        ? Math.floor(lateGameStartTimestamp / 60000) || playerFarm.length - 1
        : playerFarm.length - 1;

  const allFarmData = playerFarm.map((d, i) => {
    if (i === 0) {
      return {
        min: 0,
        gainPlayer: 0,
        gainGoal: 0,
      };
    }
    const gainPlayer = d.count - (playerFarm[i - 1]?.count || 0);
    const gainGoal =
      benchmarkData[i]?.creep_score?.mean +
      benchmarkData[i]?.jungle_creep_score?.mean -
      (benchmarkData[i - 1]?.creep_score?.mean +
        benchmarkData[i - 1]?.jungle_creep_score?.mean);

    return {
      min: Math.floor(d.timestamp / 60000),
      gainPlayer,
      gainGoal,
    };
  });

  const phaseData = allFarmData.slice(phaseStart, phaseEnd);
  const playerData = phaseData.map((d) => ({
    xData: d.min,
    yData: d.gainPlayer,
  }));
  const goalData = phaseData.map((d) => ({
    xData: d.min,
    yData: d.gainGoal,
  }));
  const allData = [...playerData, ...goalData];

  const minGraphValue = Math.min(...allData.map((d) => d.yData));
  const maxGraphValue = Math.max(...allData.map((d) => d.yData));

  return (
    <FarmingContent>
      <div className="header">
        <nav className="buttons type-caption--semi">
          {displayedPhaseOptions.map((p) => (
            <button
              key={p.key}
              onClick={() => {
                setSelectedPhase(p.key);
                sendInteractionEvent("post-match-coaching", {
                  type: "farm-phase-click",
                });
              }}
              data-active={selectedPhase === p.key}
            >
              {t(...p.label)}
            </button>
          ))}
        </nav>
        <div className="legend type-caption">
          <div className="legend-item">
            <div
              className="legend-dot"
              style={{ "--color": playerRankColor }}
            />
            <div className="legend-label">
              {t("lol:matchTile.cs", "{{cs}} CS", {
                cs: t(...playerRankName),
              })}
            </div>
          </div>
          <div className="legend-item">
            <div className="legend-dot" />
            <div className="legend-label">{t("", "Your CS")}</div>
          </div>
        </div>
      </div>
      <Chart
        xField="xData"
        yField="yData"
        xAxisConf={{
          show: true,
          type: "linear",
          tickValues: playerData.map((d) => d.xData),
          tickFormat: (value) => value.toString(),
        }}
        yAxisConf={{
          show: true,
          type: "linear",
          orientation: "right",
          tickCount: 5,
          tickValues: [
            // minGraphValue * 0.8,
            0, // Only using 0 here because this is a known reasonable vaue
            (minGraphValue + maxGraphValue) / 4,
            (minGraphValue + maxGraphValue) / 2,
            (minGraphValue + maxGraphValue) * 0.75,
            maxGraphValue * 1.2,
          ],
          color: "var(--shade1)",
        }}
        data={playerData}
        height={200}
        margin={{
          top: 0,
          right: 15,
          bottom: 15,
          left: 0,
        }}
      >
        <AreaWithGradient
          data={goalData}
          color={playerRankColor || "var(--green)"}
          xScale={undefined}
          yScale={undefined}
          xField={undefined}
          yField={undefined}
          className={undefined}
          clipPathId={undefined}
          gradientId="benchmark-fill"
          opacity={0.1}
        />
        <Line
          className={""}
          style={{
            stroke: playerRankColor || "var(--green)",
            strokeWidth: 2,
          }}
          data={goalData}
          xScale={undefined}
          yScale={undefined}
          xField={undefined}
          yField={undefined}
          clipPathId={undefined}
        />
        {/* <AreaWithGradient
          data={playerData}
          color={"var(--shade0)"}
          xScale={undefined}
          yScale={undefined}
          xField={undefined}
          yField={undefined}
          className={undefined}
          clipPathId={undefined}
          gradientId="player-fill"
          opacity={0.1}
        /> */}
        <Line
          className={""}
          style={{ stroke: "var(--shade0)", strokeWidth: 2 }}
          data={playerData}
          xScale={undefined}
          yScale={undefined}
          xField={undefined}
          yField={undefined}
          clipPathId={undefined}
        />
      </Chart>
      <span
        className="type-caption--semi"
        style={{
          textAlign: "center",
          paddingInline: "var(--sp-3)",
          color: "var(--shade1-75)",
        }}
      >
        {t(
          "lol:coaching.csChangeSincePreviousMinute",
          "CS Increase/Decrease since previous minute",
        )}
      </span>
    </FarmingContent>
  );
}

export function performanceFeedback({
  match,
  playerRankDetails,
  playerPerformance,
  localPlayer,
  playerDeaths,
  playerFarm,
  benchmarkData,
  lateGameStartTimestamp,
}: {
  match: Match;
  playerRankDetails: any /* eslint-disable-line @typescript-eslint/no-explicit-any */;
  playerPerformance: PlayerPerformance;
  localPlayer: MatchParticipant;
  playerDeaths: DeathOutcome[];
  playerFarm: FarmSnapshot[];
  benchmarkData: any /* eslint-disable-line @typescript-eslint/no-explicit-any */;
  lateGameStartTimestamp: number;
}) {
  const nextRankSymbol = playerRankDetails?.nextRank;
  const nextRankDetails = nextRankSymbol
    ? RANK_SYMBOL_TO_STR[nextRankSymbol]
    : null;

  const combat = playerPerformance.categories.find((c) => c.key === "combat");
  if (combat) {
    combat.feedback.title = (
      <PlayerDeathsTitle
        matchMins={Math.floor(match.gameDuration / 60)}
        playerDeaths={playerDeaths}
        playerRank={
          playerRankDetails && [
            playerRankDetails.t.name,
            playerRankDetails.t.fallback,
          ]
        }
        playerNextRank={
          nextRankDetails && [
            nextRankDetails.t.name,
            nextRankDetails.t.fallback,
          ]
        }
        benchmarkData={benchmarkData}
      />
    );
    combat.feedback.titleHighlightColor = "var(--yellow)";
    combat.feedback.content = (
      <MinimapDeathsTimeline
        championId={localPlayer.championId}
        deaths={playerDeaths}
      />
    );

    const winProbabilityChange = [...playerDeaths].sort(
      (a, b) => a.winProbabilityChange - b.winProbabilityChange,
    )[0]?.winProbabilityChange;
    combat.impact = winProbabilityChange;
  }

  const farming = playerPerformance.categories.find((c) => c.key === "farming");
  if (farming) {
    farming.feedback.title = <PlayerFarmTitle data={farming} />;
    farming.feedback.titleHighlightColor = "var(--yellow)";
    farming.feedback.content = (
      <PlayerFarmContent
        data={farming}
        playerFarm={playerFarm}
        benchmarkData={benchmarkData}
        playerRankName={
          playerRankDetails && [
            playerRankDetails.t.name,
            playerRankDetails.t.fallback,
          ]
        }
        playerRankColor={playerRankDetails?.color}
        lateGameStartTimestamp={lateGameStartTimestamp}
      />
    );
    const mostImpactfulPhase = getMostImpactfulFarmingPhase(farming);
    const winProbabilityWith = 0.5;
    const winProbabilityActual = mostImpactfulPhase
      ? calcWinProbability(
          Math.floor(mostImpactfulPhase.misc.timestamp / 60000),
          mostImpactfulPhase.misc.goldValue,
        )
      : 0;
    const winProbabilityChange = winProbabilityActual - winProbabilityWith;
    farming.impact = winProbabilityChange;
  }
}
